import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TeamInfo } from '../TeamInfo';
import { RaceRules } from '../types';
import { LivePlan } from '../analytics/liveStrategy';
import {
  Alert,
  AlertContext,
  AlertKind,
  detectAlerts,
  FiredAlerts,
} from './detect';
import { AlertSound, createAlertSound } from './sound';

const SETTINGS_KEY = 'karting-timing-alerts';

/** Beyond this the strip stops being glanceable, so the oldest drop off. */
const MAX_VISIBLE = 6;

export const ALERT_KINDS: { kind: AlertKind; label: string }[] = [
  { kind: 'pit-due', label: 'Pit window' },
  { kind: 'pit-over', label: 'Over stint limit' },
  { kind: 'pit-entered', label: 'Entered pit' },
  { kind: 'pit-exited', label: 'Left pit' },
  { kind: 'penalty', label: 'Penalties' },
  { kind: 'drive-time', label: 'Drive time' },
  { kind: 'session', label: 'Countdown' },
  { kind: 'plan-stop-due', label: 'Planned stop coming up' },
  { kind: 'plan-stop-now', label: 'Planned stop — box now' },
  { kind: 'plan-stop-overdue', label: 'Planned stop overdue' },
  { kind: 'plan-driver-mismatch', label: 'Not the planned driver' },
  { kind: 'plan-infeasible', label: 'Plan can no longer be followed' },
];

export interface AlertSettings {
  enabled: boolean;
  sound: boolean;
  kinds: Record<AlertKind, boolean>;
}

const DEFAULT_SETTINGS: AlertSettings = {
  enabled: true,
  // Off by default: a pit wall that beeps before anyone asked for it gets muted
  // permanently within one stint.
  sound: false,
  kinds: {
    'pit-due': true,
    'pit-over': true,
    'pit-entered': true,
    'pit-exited': true,
    penalty: true,
    'drive-time': true,
    session: true,
    'plan-stop-due': true,
    'plan-stop-now': true,
    'plan-stop-overdue': true,
    'plan-driver-mismatch': true,
    'plan-infeasible': true,
  },
};

function loadSettings(): AlertSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (!stored) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(stored) as Partial<AlertSettings>;
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      // Merged rather than replaced so a kind added later defaults to on
      // instead of vanishing for anyone with saved settings.
      kinds: { ...DEFAULT_SETTINGS.kinds, ...(parsed.kinds ?? {}) },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export interface UseAlertsOptions {
  teams: Map<string, TeamInfo>;
  watchedTeamIds: Set<string>;
  rules: RaceRules;
  penaltyCountByKart: Map<string, number>;
  minutesRemaining: number | null;
  /** Stint plans reconciled against the race, per team. */
  livePlans?: Map<string, LivePlan>;
  /**
   * Alerts are for a live race. A replay is deliberately excluded: at 60× a
   * six-hour recording would fire hundreds of them in a few minutes.
   */
  active: boolean;
}

export interface UseAlertsResult {
  alerts: Alert[];
  settings: AlertSettings;
  setEnabled: (enabled: boolean) => void;
  setSound: (sound: boolean) => void;
  toggleKind: (kind: AlertKind) => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
}

export function useAlerts({
  teams,
  watchedTeamIds,
  rules,
  penaltyCountByKart,
  minutesRemaining,
  livePlans,
  active,
}: UseAlertsOptions): UseAlertsResult {
  const [settings, setSettings] = useState<AlertSettings>(loadSettings);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const firedRef = useRef<FiredAlerts>(new Set());
  const soundRef = useRef<AlertSound | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // A full or blocked store is not worth failing an alert over.
    }
  }, [settings]);

  useEffect(() => () => soundRef.current?.close(), []);

  const context = useMemo<AlertContext>(
    () => ({
      teams,
      watchedTeamIds,
      rules,
      penaltyCountByKart,
      minutesRemaining,
      livePlans,
      now: Date.now(),
    }),
    [teams, watchedTeamIds, rules, penaltyCountByKart, minutesRemaining, livePlans],
  );

  useEffect(() => {
    if (!active || !settings.enabled) return;

    const found = detectAlerts(firedRef.current, context).filter(
      (a) => settings.kinds[a.kind] !== false,
    );
    if (found.length === 0) return;

    if (settings.sound) {
      // One tone per batch, at the highest severity in it — three separate
      // beeps for three simultaneous alerts is just noise.
      const severity = found.some((a) => a.severity === 'urgent')
        ? 'urgent'
        : found.some((a) => a.severity === 'warn')
          ? 'warn'
          : 'info';
      soundRef.current?.play(severity);
    }

    setAlerts((prev) => [...found.reverse(), ...prev].slice(0, MAX_VISIBLE));
  }, [context, active, settings]);

  const setEnabled = useCallback((enabled: boolean) => {
    setSettings((prev) => ({ ...prev, enabled }));
  }, []);

  const setSound = useCallback((sound: boolean) => {
    if (sound) {
      // Must happen inside the click that enabled it, or the browser blocks
      // audio and the first real alert is silent.
      soundRef.current = soundRef.current ?? createAlertSound();
      soundRef.current.unlock();
    }
    setSettings((prev) => ({ ...prev, sound }));
  }, []);

  const toggleKind = useCallback((kind: AlertKind) => {
    setSettings((prev) => ({
      ...prev,
      kinds: { ...prev.kinds, [kind]: !prev.kinds[kind] },
    }));
  }, []);

  // An alert never returns once fired, so dropping it from the list is enough.
  const dismiss = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAll = useCallback(() => setAlerts([]), []);

  return { alerts, settings, setEnabled, setSound, toggleKind, dismiss, clearAll };
}
